import { useEffect, useState } from 'react';
import { Outlet, useNavigate, useLocation, NavLink } from 'react-router-dom';
import {
  IconBuilding,
  IconUsers,
  IconLogout,
  IconLayoutDashboard,
  IconShield,
  IconUserCircle,
  IconBriefcase,
  IconFolderOpen,
  IconClipboardList,
  IconHeartbeat,
  IconSearch,
  IconMenu2,
  IconX,
} from '@tabler/icons-react';
import Cookies from 'js-cookie';
import { useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { useKeyboardShortcuts } from '@/hooks/useKeyboardShortcuts';
import Routes from '@/router/routes';
import TelletLogo from '@/assets/logo/tellet-logo-main.svg';

export default function SuperAdminWrapper() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, logout, loadUser, superAdmin, isLoading } = useAuthStore();
  const queryClient = useQueryClient();
  const [isInitializing, setIsInitializing] = useState(true);
  const [globalSearch, setGlobalSearch] = useState('');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  useKeyboardShortcuts();

  useEffect(() => {
    const initAuth = async () => {
      const token = Cookies.get('admin_token');
      if (!token) {
        navigate(Routes.login.path, { replace: true });
        setIsInitializing(false);
        return;
      }

      try {
        if (!superAdmin) {
          await loadUser();
        }
      } finally {
        setIsInitializing(false);
      }
    };

    initAuth();
  }, [navigate, loadUser, superAdmin]);

  useEffect(() => {
    if (!isInitializing && !isLoading && !isAuthenticated) {
      navigate(Routes.login.path, { replace: true });
    }
  }, [isInitializing, isLoading, isAuthenticated, navigate]);

  // Show loading state while initializing authentication
  if (isInitializing || isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) return null;

  const handleLogout = async () => {
    await logout();
    queryClient.clear();
    navigate(Routes.login.path);
  };

  const handleGlobalSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (!globalSearch.trim()) return;
    const searchablePages = [
      Routes.super_admin_dashboard_users.path,
      Routes.super_admin_dashboard_organizations.path,
      Routes.super_admin_dashboard_workspaces.path,
      Routes.super_admin_dashboard_projects.path,
      Routes.super_admin_dashboard_admins.path,
    ];
    const currentPath = location.pathname;
    const targetPath = searchablePages.find(p => currentPath.startsWith(p))
      || Routes.super_admin_dashboard_users.path;
    navigate(`${targetPath}?search=${encodeURIComponent(globalSearch.trim())}`);
    setGlobalSearch('');
  };

  const navItems = [
    {
      to: Routes.super_admin_dashboard.path,
      icon: IconLayoutDashboard,
      label: 'Dashboard',
    },
    {
      to: Routes.super_admin_dashboard_organizations.path,
      icon: IconBuilding,
      label: 'Organizations',
    },
    {
      to: Routes.super_admin_dashboard_workspaces.path,
      icon: IconBriefcase,
      label: 'Workspaces',
    },
    {
      to: Routes.super_admin_dashboard_projects.path,
      icon: IconFolderOpen,
      label: 'Projects',
    },
    {
      to: Routes.super_admin_dashboard_users.path,
      icon: IconUsers,
      label: 'Users',
    },
    {
      to: Routes.super_admin_dashboard_admins.path,
      icon: IconShield,
      label: 'Super Admins',
    },
    {
      to: Routes.super_admin_dashboard_audit_log.path,
      icon: IconClipboardList,
      label: 'Audit Log',
    },
    {
      to: Routes.super_admin_dashboard_system_health.path,
      icon: IconHeartbeat,
      label: 'System Health',
    },
    {
      to: Routes.super_admin_dashboard_account.path,
      icon: IconUserCircle,
      label: 'My Account',
    },
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-card border-b border-border sticky top-0 z-50">
        <div className="flex items-center justify-between px-6 py-3">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="md:hidden p-2 text-muted-foreground hover:text-foreground"
            >
              <IconMenu2 size={20} />
            </button>
            <img src={TelletLogo} alt="Tellet" className="h-8 dark:invert" />
            <span className="text-lg font-semibold text-foreground">Super Admin</span>
          </div>
          <form onSubmit={handleGlobalSearch} className="flex-1 max-w-md mx-4">
            <div className="relative">
              <IconSearch className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" size={16} />
              <input
                type="text"
                placeholder="Search users, orgs... ( / )"
                value={globalSearch}
                onChange={(e) => setGlobalSearch(e.target.value)}
                data-global-search
                className="w-full pl-9 pr-3 py-1.5 text-sm border border-border rounded-md bg-muted text-foreground focus:bg-background focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors placeholder:text-muted-foreground"
              />
            </div>
          </form>
          <div className="flex items-center gap-4">
            {superAdmin && (
              <span className="text-sm text-muted-foreground hidden sm:block">{superAdmin.email}</span>
            )}
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
            >
              <IconLogout size={18} />
              Logout
            </button>
          </div>
        </div>
      </header>

      <div className="flex h-[calc(100vh-57px)]">
        {/* Sidebar - desktop */}
        <aside className="hidden md:block w-64 bg-card border-r border-border overflow-y-auto flex-shrink-0">
          <nav className="p-4 space-y-1">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === Routes.super_admin_dashboard.path}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    isActive
                      ? 'bg-primary/10 text-primary'
                      : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                  }`
                }
              >
                <item.icon size={20} />
                {item.label}
              </NavLink>
            ))}
          </nav>
        </aside>

        {/* Mobile sidebar overlay */}
        {sidebarOpen && (
          <div className="fixed inset-0 z-40 md:hidden">
            <div className="fixed inset-0 bg-black/50" onClick={() => setSidebarOpen(false)} />
            <aside className="fixed left-0 top-0 h-full w-64 bg-card z-50 shadow-lg">
              <div className="p-4">
                <button onClick={() => setSidebarOpen(false)} className="mb-4 text-muted-foreground">
                  <IconX size={20} />
                </button>
                <nav className="space-y-1">
                  {navItems.map((item) => (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      end={item.to === Routes.super_admin_dashboard.path}
                      onClick={() => setSidebarOpen(false)}
                      className={({ isActive }) =>
                        `flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                          isActive
                            ? 'bg-primary/10 text-primary'
                            : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                        }`
                      }
                    >
                      <item.icon size={20} />
                      {item.label}
                    </NavLink>
                  ))}
                </nav>
              </div>
            </aside>
          </div>
        )}

        {/* Main Content */}
        <main className="flex-1 p-6 overflow-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
